import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
  applyRaceUpdate,
  myRacesUpdates,
  raceStandingUpdate,
  raceUpdate,
  setLoading
} from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { select } from '@swf/core/store';
import type {
  ExtractSelectFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type { CasinoRace } from '@swf/core/types/CasinoRaces';
import type { TranslationTag } from '@swf/core/types/i18n';
import { canUseDOM } from '@swf/core/utils';
import { currencyChange, login } from '@swf/core/utils/actions';
import type { CasinoGameService } from '@swf/generated/types/common/Casino';
import type { CasinoRacesWidgetConfig } from '@swf/generated/types/widgets/CasinoRacesWidget';

import { TXTWCL_My_Races, TXTWCL_Next_Races } from './model.messages.json';

/* eslint-disable no-console */

export interface CasinoRacesWidgetState {
  userCompleted?: CasinoRace[];
  subscriptions: { [path: string]: string };
  active: TranslationTag['id'];
  knownRaces: Record<string, number>;
  races?: CasinoRace[];
  loading: boolean;
}

const reducers = {
  applyRaceUpdate,
  set: (state: CasinoRacesWidgetState, payload: Partial<CasinoRacesWidgetState>) => {
    let { knownRaces } = state;
    if (payload.races) {
      knownRaces = {};
      payload.races?.forEach((race, index) => {
        knownRaces[race.id] = index;
      });
    }

    return {
      ...state,
      ...payload,
      knownRaces
    };
  },
  setLoading,
  raceStandingUpdate,
  setActive: (state: CasinoRacesWidgetState, payload: TranslationTag['id']) => ({
    ...state,
    active: payload
  })
};

const selectors = (slice: Slicer<CasinoRacesWidgetState>) => ({
  get() {
    return slice;
  },

  getCompleted() {
    return slice((state) => state.userCompleted || ([] as CasinoRace[]));
  },

  isMyRaces() {
    return slice((state) => state.active === TXTWCL_My_Races.id);
  },

  isLoading() {
    return slice((state) => {
      const result = !(state.races || state.userCompleted);
      return result;
    });
  }
});

let fetchAllSubject: Subject<void>;
const releaseSubject = () => {
  if (fetchAllSubject) {
    fetchAllSubject.next();
    fetchAllSubject.complete();
  }
};

const effects = {
  myRacesUpdates,
  raceUpdate,
  async preload(payload: undefined, params: WidgetEffectParams<CasinoRacesWidgetConfig>) {
    const { widget, dispatch, actions, rootState } = params;
    const widgetDispatch = dispatch[widget.id];
    const clientAuthenticated = select.session.isClientAuthenticated(rootState);
    const widgetSelect: ExtractSelectFromModel<CasinoRacesWidgetModel> = select[widget.id];

    await widgetDispatch.fetchAll();

    currencyChange(actions)?.subscribe(({ state }) => {
      widgetDispatch.set({ all: undefined, user: undefined, userCompleted: undefined });
      widgetDispatch.fetchAll();
    });

    login(actions)?.subscribe(async () => {
      widgetDispatch.myRacesUpdates();
      const data = widgetSelect.get(rootState);
      Object.entries(data.knownRaces).forEach(([key]) => widgetDispatch.raceUpdate(key));
    });

    if (clientAuthenticated) {
      widgetDispatch.myRacesUpdates();
    }
  },

  async fetchByActive(
    payload: TranslationTag['id'],
    params: WidgetEffectParams<CasinoRacesWidgetConfig>
  ) {
    const { widget, dispatch } = params;
    const widgetDispatch = dispatch[widget.id];
    if (payload === TXTWCL_My_Races.id) {
      widgetDispatch.fetchUser();
      widgetDispatch.fetchCompleted();
    } else {
      widgetDispatch.fetchAll();
    }
  },

  async setActive(
    payload: TranslationTag['id'],
    params: WidgetEffectParams<CasinoRacesWidgetConfig>
  ) {
    const { widget, dispatch } = params;
    const widgetDispatch = dispatch[widget.id];
    widgetDispatch.set({ active: payload });
    widgetDispatch.fetchByActive(payload);
  },

  async fetchRaceList(
    payload: { service: CasinoGameService; field: string },
    params: WidgetEffectParams<CasinoRacesWidgetConfig>
  ) {
    const {
      widget,
      dispatch,
      request,
      rootState,
      widget: { config }
    } = params;
    const widgetDispatch = dispatch[widget.id];
    const clientAuthenticated = select.session.isClientAuthenticated(rootState);

    releaseSubject();

    if (clientAuthenticated) {
      const { field } = payload;
      const body = config[`${field}`].params;
      const service = config[`${field}`].config.name;

      request({ service, body }).then((result) => {
        widgetDispatch.set({ [field === 'user' ? 'races' : field]: result.body });
      });
    }
  },

  async fetchAll(payload: undefined, params: WidgetEffectParams<CasinoRacesWidgetConfig>) {
    const {
      actions,
      widget,
      dispatch,
      select,
      request,
      requestStream,
      rootState,
      widget: { id, config }
    } = params;
    const widgetDispatch = dispatch[widget.id];
    const widgetSelect: ExtractSelectFromModel<CasinoRacesWidgetModel> = select[id];

    const options = {
      service: config.all?.config.name as CasinoGameService,
      body: config.all?.params
    };
    widgetDispatch.set({ loading: true });

    if (!canUseDOM) {
      const { body: data } = await request<CasinoRacesWidgetState>(options);
      widgetDispatch.set({ ...data, loading: false });
    } else {
      const data = widgetSelect.get(rootState);
      let initialData;
      releaseSubject();
      if (!fetchAllSubject) {
        initialData = data?.races?.length ? { ...data } : undefined;
      }

      fetchAllSubject = new Subject<void>();

      requestStream<CasinoRacesWidgetState>(options, initialData)
        .pipe(takeUntil(currencyChange(actions) as typeof actions))
        .pipe(takeUntil(fetchAllSubject))
        .subscribe((update) => {
          widgetDispatch.set({ ...update, loading: false });
          update.races?.forEach((race: CasinoRace) => {
            widgetDispatch.raceUpdate(race.id);
          });
        });
    }
  },

  async fetchUser(payload: undefined, params: WidgetEffectParams<CasinoRacesWidgetConfig>) {
    const { widget, dispatch } = params;
    const widgetDispatch = dispatch[widget.id];
    widgetDispatch.fetchRaceList({ field: 'user' });
  },

  async fetchCompleted(payload: undefined, params: WidgetEffectParams<CasinoRacesWidgetConfig>) {
    const { widget, dispatch } = params;
    const widgetDispatch = dispatch[widget.id];
    widgetDispatch.fetchRaceList({ field: 'userCompleted' });
  }
};

export interface CasinoRacesWidgetModel
  extends WidgetModelConfig<CasinoRacesWidgetConfig, CasinoRacesWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const racesWidgetModel: CasinoRacesWidgetModel = {
  state: {
    active: TXTWCL_Next_Races.id,
    loading: false,
    knownRaces: {},
    subscriptions: {}
  },
  reducers,
  effects,
  selectors
};

export const useRacesWidgetModel: UseWidget<CasinoRacesWidgetConfig, CasinoRacesWidgetModel> =
  useWidget;
